import { createServerFn } from "@tanstack/react-start";

import { authMiddleware } from "~/lib/auth-middleware";
import {
  startConversationSchema,
  startCoachConversationSchema,
  sendReplySchema,
  getConversationsSchema,
  getConversationSchema,
  getEngagementConversationSchema,
  markConversationReadSchema,
  findConversationWithUserSchema,
} from "~/lib/schemas/messaging";
import { UserFacingError } from "~/lib/shared/user-facing-error";

export const startConversation = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(startConversationSchema)
  .handler(async ({ context, data }) => {
    const { startConversationInternal } =
      await import("~/lib/server/messaging");
    return startConversationInternal({
      fromUserId: context.user.id,
      toUserId: data.recipientUserId,
      subject: data.subject,
      content: data.content,
    });
  });

/**
 * The contact form on a coach's profile. The coach's user id stays on the
 * server: the visitor names the coach by username and this resolves who
 * receives the message.
 */
export const startConversationWithCoach = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(startCoachConversationSchema)
  .handler(async ({ context, data }) => {
    const { getPublicCoachUserId } =
      await import("~/lib/server/coach-visibility");
    const toUserId = await getPublicCoachUserId(data.username);
    if (!toUserId) {
      throw new UserFacingError("This coach is not set up to receive messages");
    }
    const { startConversationInternal } =
      await import("~/lib/server/messaging");
    return startConversationInternal({
      fromUserId: context.user.id,
      toUserId,
      subject: data.subject,
      content: data.content,
    });
  });

export const getMyConversations = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(getConversationsSchema)
  .handler(async ({ context, data }) => {
    const { getConversationsForUser } = await import("~/lib/server/messaging");
    return getConversationsForUser(context.user.id, data.limit, data.offset);
  });

export const getConversation = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(getConversationSchema)
  .handler(async ({ context, data }) => {
    const { getConversationMessages } = await import("~/lib/server/messaging");
    return getConversationMessages(
      data.conversationId,
      context.user.id,
      data.limit,
      data.offset,
    );
  });

export const sendReply = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(sendReplySchema)
  .handler(async ({ context, data }) => {
    const { sendReplyInternal } = await import("~/lib/server/messaging");
    return sendReplyInternal(
      data.conversationId,
      context.user.id,
      data.content,
    );
  });

export const markConversationRead = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(markConversationReadSchema)
  .handler(async ({ context, data }) => {
    const { markConversationReadInternal } =
      await import("~/lib/server/messaging");
    return markConversationReadInternal(data.conversationId, context.user.id);
  });

export const getWaitingEnquiries = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getWaitingEnquiriesInternal } =
      await import("~/lib/server/messaging");
    return getWaitingEnquiriesInternal(context.user.id);
  });

export const getEngagementConversation = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(getEngagementConversationSchema)
  .handler(async ({ context, data }) => {
    const { getEngagementConversationInternal } =
      await import("~/lib/server/messaging");
    return getEngagementConversationInternal(
      context.user.id,
      data.engagementId,
    );
  });

export const findConversationWithUser = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(findConversationWithUserSchema)
  .handler(async ({ context, data }) => {
    const { findConversationWithUserInternal } =
      await import("~/lib/server/messaging");
    return findConversationWithUserInternal(context.user.id, data.userId);
  });
